import React, { useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useResetPassword } from "../../hooks/queries/useAuthMutations";

// The backend emails a link to APP_URL/reset-password?token=...; on HashRouter
// (native) builds the token may live inside the hash instead.
const readTokenFromHash = () => {
  const hash = window.location.hash || "";
  if (!hash.includes("reset-password")) return "";
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) return "";
  return new URLSearchParams(hash.slice(queryIndex + 1)).get("token") || "";
};

const ResetPasswordPage = ({ onPasswordReset }) => {
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => searchParams.get("token") || readTokenFromHash(),
    [searchParams],
  );
  return <ResetPasswordForm key={token} token={token} onPasswordReset={onPasswordReset} />;
};

const ResetPasswordForm = ({ token, onPasswordReset }) => {
  const navigate = useNavigate();
  const sessionReady = !!token;

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [success, setSuccess] = useState(false);
  const resetPassword = useResetPassword();
  const isLoading = resetPassword.isPending;

  const submitting = useRef(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || success) return;
    setError("");
    setLinkExpired(false);

    if (!password || !passwordConfirm) {
      setError("Ambos campos son obligatorios.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    submitting.current = true;
    try {
      await resetPassword.mutateAsync({ token, password });
      setSuccess(true);
    } catch (err) {
      if (
        err?.code === "unauthorized" ||
        err?.code === "invalid_token" ||
        err?.details?.code === "invalid_token"
      ) {
        setLinkExpired(true);
        setError(
          "El enlace de recuperación es inválido o ha expirado. Solicita un nuevo enlace.",
        );
      } else if (err?.code === "API_UNREACHABLE") {
        setError("Error de conexión. Inténtalo de nuevo.");
      } else {
        setError(err?.message || "Error de conexión. Inténtalo de nuevo.");
      }
    } finally { submitting.current = false; }
  };

  if (!sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-4">
            El enlace de recuperación es inválido o ha expirado.
          </p>
          <button
            onClick={() => navigate("/forgot-password")}
            className="text-sm font-semibold text-blue-900 hover:underline underline-offset-2 transition-colors"
          >
            Solicitar nuevo enlace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {!success ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Title */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <Lock size={28} className="text-blue-950" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-wide mb-2">
                    Nueva contraseña
                  </h1>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Ingresa tu nueva contraseña. Debe tener al menos 8
                    caracteres.
                  </p>
                </div>

                {/* Error */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                    {linkExpired && (
                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="block mt-2 text-sm font-semibold text-blue-900 hover:underline underline-offset-2 transition-colors"
                      >
                        Solicitar nuevo enlace
                      </button>
                    )}
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* New Password */}
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Nueva contraseña*"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {/* Confirm Password */}
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      placeholder="Confirmar nueva contraseña*"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordConfirm(!showPasswordConfirm)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPasswordConfirm ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>

                  {/* Submit */}
                  <div className="flex justify-center pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          Restablecer contraseña
                          <span className="text-base">✦</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Success State */}
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={28} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide mb-2">
                    ¡Contraseña actualizada!
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-8">
                    Tu contraseña ha sido restablecida exitosamente. Ya puedes
                    iniciar sesión con tu nueva contraseña.
                  </p>

                  <button
                    onClick={onPasswordReset}
                    className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 inline-flex items-center gap-3"
                  >
                    Ir a iniciar sesión
                    <span className="text-base">✦</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
